/* eslint-disable no-underscore-dangle */
import { BillDetailed } from '@enview/interface/types/bills/Bill';
import { faGlobeAmericas } from '@fortawesome/free-solid-svg-icons/faGlobeAmericas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Analytics from '../../analytics/BillAnalytics';
import { BillAPI } from '../../api';
import { SimilarBillHit } from '../../api/bill';
import { g as abilityGlossary } from '../../config/ability';
import { State } from '../../dux/@types';
import { checkPermission } from '../../dux/ModalDux';
import { getJurisdictionsForTeamMode } from '../../dux/TeamModeDux';
import featureFlags, { FrontendFeature } from '../../featureFlags';
import { userTeamIsTracking } from '../../helpers/BillHelper';
import STATES from '../../helpers/states';
import FormPopover from '../Shared/Popovers/FormPopover';
import JurisdictionSelector from '../Shared/Selectors/JurisdictionSelector';
import { Link } from 'react-router-dom';

type SimilarBillsProps = {
  bill: BillDetailed;
  versionId?: number;
};

const SimilarBills = (props: SimilarBillsProps): ReactElement => {
  const { bill, versionId } = props;

  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [legislatureFiltersVisible, setLegislatureFilterVisible] = useState(false);
  const [showMoreBillTextMatches, setShowMoreBillTextMatches] = useState(false);

  const dispatch = useDispatch();

  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const orgJurisdictions = useSelector(getJurisdictionsForTeamMode);
  const [getSimilarBills, { isError, data: similarBills }] =
    BillAPI.endpoints.getSimilarBills.useMutation();

  const getSimilar = async (): Promise<void> => {
    setLegislatureFilterVisible(false);
    if (
      versionId &&
      orgUser &&
      (userTeamIsTracking(bill, orgUser) ||
        dispatch(
          checkPermission(abilityGlossary.SEARCH, abilityGlossary.BILL_INTELLIGENCE),
        ))
    ) {
      const { state } = bill;
      let searchTypes: string[] = [];
      if (
        orgUser &&
        featureFlags.isFeatureEnabledForUser(
          FrontendFeature.BillSimilarBillsRelevanceSwitch,
          orgUser,
        )
      ) {
        searchTypes = ['searchByText', 'searchByName'];
      } else {
        // Check if it's intra- or inter- state
        searchTypes = jurisdictions.find((jurisdiction) => jurisdiction === state)
          ? ['searchByText']
          : ['searchByName'];
      }
      await getSimilarBills({
        bill,
        versionId,
        jurisdictions,
        orgUser,
        searchTypes,
      });
    }
  };

  const renderJurisdiction = (billVersionDoc: {
    jurisdiction_abbreviation: string;
  }): string => {
    const stateAbbreviation = billVersionDoc.jurisdiction_abbreviation
      ? billVersionDoc.jurisdiction_abbreviation.toUpperCase()
      : 'USA';
    return STATES[stateAbbreviation];
  };

  const renderJurisdictionsFilter = (): ReactElement => {
    const toggleCallback = (): void => {
      setLegislatureFilterVisible(!legislatureFiltersVisible);
    };

    const targetButton = (
      <button
        className="btn btn-primary rounded start"
        onClick={toggleCallback}
        type="button"
      >
        Begin Search
      </button>
    );

    return (
      <FormPopover
        addClass="jurisdiction-selector form-container"
        addInnerClass="form-inner-class"
        align="end"
        hideCallback={getSimilar}
        label="Legislature"
        show={legislatureFiltersVisible}
        targetButton={targetButton}
        toggleCallback={toggleCallback}
      >
        <JurisdictionSelector
          jurisdictions={orgJurisdictions}
          onSelectionChange={setJurisdictions}
          saveButtonProps={{
            label: 'Find Bills',
            type: 'button',
            onClick: getSimilar,
          }}
          selectedFilters={jurisdictions}
        />
      </FormPopover>
    );
  };

  const handleSimilarBillResultClick = (billId: string, jurisdiction: string): void => {
    if (!similarBills) return;
    const queryData = JSON.stringify(similarBills[0].meta);
    Analytics.trackSimilarBillsResultViewed(billId, jurisdiction, queryData, orgUser);
  };

  // Reduce similar bill-versions to an array that represents each unique bill
  // and sort by score

  const processedSimilarBills =
    similarBills &&
    similarBills.map((bills) => {
      return bills.result.hits.hits
        ? {
            meta: bills.meta,
            result: bills.result.hits.hits
              .reduce<SimilarBillHit[]>((uniqueHits, hit) => {
                const existingHit = uniqueHits.find(
                  (h) => h._source.bill_id === hit._source.bill_id,
                );
                if (!existingHit) {
                  uniqueHits.push(hit);
                } else if (hit._score > existingHit._score) {
                  // always keep representative bill-version hit with highest score
                  const existingIndex = uniqueHits.findIndex(
                    (h) => h._source.bill_id === hit._source.bill_id,
                  );
                  uniqueHits.splice(existingIndex, 1);
                  uniqueHits.push(hit);
                }
                return uniqueHits;
              }, [])
              .filter((hit) => hit._source.bill_id !== bill.id)
              .sort((a, b) => b._score - a._score),
          }
        : undefined;
    });

  const numBillTextMatchesToShow = (similarBillsIndex: number): number => {
    return showMoreBillTextMatches &&
      processedSimilarBills &&
      processedSimilarBills[similarBillsIndex]
      ? processedSimilarBills[similarBillsIndex]?.result.length ?? 0
      : 10;
  };

  const displayMetaKeys = [
    'excludeOmnibus',
    'jurisdictions',
    'searchByText',
    'searchByName',
    'sessions',
    'similarToBillVersion',
  ];

  return (
    <div className="similar-bills react-sanfona-item">
      {/* <ReactTooltip effect="solid" /> */}
      <h3 className="react-sanfona-item-title">
        <span className="py-1">
          {' '}
          <FontAwesomeIcon icon={faGlobeAmericas} /> Global Bill Search
        </span>
        <span className="float-right">{renderJurisdictionsFilter()}</span>
        {/* {' '}<span data-tip="Bills that are related in other jurisdictions"><FontAwesomeIcon icon={faInfoCircle} /></span> */}
      </h3>

      {processedSimilarBills &&
        processedSimilarBills.map((processedBills, billIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="react-sanfona-item-body" key={billIndex}>
            {processedBills && processedBills.result.length > 0 && (
              <div className="react-sanfona-item-body-wrapper">
                <p>
                  <strong>
                    Plural found similarities in the jurisdictions you&rsquo;ve
                    selected:
                  </strong>
                </p>

                {featureFlags.isFeatureEnabledForUser(
                  FrontendFeature.BillSimilarBillsRelevanceSwitch,
                  orgUser,
                ) && (
                  <p>
                    <span>
                      Total results: {processedBills.result.length}
                      <br />
                    </span>
                    {displayMetaKeys.map((key, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={index}>
                        {processedBills.meta[key] && (
                          <span>
                            {key}:{' '}
                            <u>
                              {JSON.stringify(processedBills.meta[key])
                                .replace('","', ', ')
                                .replace(/[^,a-z|0-9]+/gi, '')}
                            </u>
                            <br />
                          </span>
                        )}
                      </span>
                    ))}
                  </p>
                )}
                <ul className="list-paragraph-style">
                  {processedBills.result
                    .slice(
                      0,
                      numBillTextMatchesToShow(
                        processedSimilarBills.indexOf(processedBills),
                      ),
                    )
                    .map((hit, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`${hit._id}${index}`}>
                        {featureFlags.isFeatureEnabledForUser(
                          FrontendFeature.BillSimilarBillsRelevanceSwitch,
                          orgUser,
                        ) && (
                          <span>
                            <span>
                              Rank: <u>#{processedBills.result.indexOf(hit) + 1}</u>
                              <br />
                            </span>
                            <span>
                              Score: <u>{hit._score}</u>
                              <br />
                            </span>
                          </span>
                        )}
                        <Link
                          onClick={() =>
                            handleSimilarBillResultClick(
                              hit._source.bill_id,
                              renderJurisdiction(hit._source),
                            )
                          }
                          rel="noopener noreferrer"
                          target="_blank"
                          to={`/legislative-tracking/bill/details/${hit._source.bill_id}`}
                        >
                          {renderJurisdiction(hit._source)} {hit._source.bill_number}
                        </Link>
                        : {hit._source.bill_name}
                      </li>
                    ))}
                </ul>

                {processedBills.result.length >
                  numBillTextMatchesToShow(
                    processedSimilarBills.indexOf(processedBills) ?? 0,
                  ) && (
                  // eslint-disable-next-line react/button-has-type
                  <button
                    className="more"
                    onClick={() => setShowMoreBillTextMatches(true)}
                    tabIndex={0}
                  >
                    show more
                  </button>
                )}
              </div>
            )}

            {(isError || !processedBills || processedBills.result.length === 0) && (
              <p>
                <strong>No similar bills found.</strong>
              </p>
            )}
          </div>
        ))}
    </div>
  );
};

export default SimilarBills;
